/**
 * Settings Directory
 *
 * The map of every settings surface, backing the settings hub page
 * (/:orgSlug/settings) and the settings paths OmniSearch deep-links to. Like
 * settings-sections.ts this is intentionally DATA ONLY (enums and plain
 * objects, no components) so it can live in the main bundle.
 *
 * Note the hub page renders the `org` and `admin` scopes only — personal
 * settings live in the user menu (My Profile / My Account). The `personal`
 * entries stay here because they map module hrefs (/tasks/mine, /calendar)
 * to their settings paths, which the path-coverage tests and the directory
 * hook both rely on.
 *
 * Section lists are NOT duplicated here. Hub entries reference the frozen
 * registries in settings-sections.ts (MODULE_SETTINGS_SECTIONS keyed by
 * navHref, ADMIN_SETTINGS_SECTIONS, USER_SETTINGS_TABS); ids and labels there
 * are load-bearing for OmniSearch and the help-content path audit in
 * packages/database.
 *
 * When a module gains or loses a settings page, update MODULE_SETTINGS_PATHS
 * and SETTINGS_DIRECTORY together.
 */

import {
  AccountType,
  ModuleTypeEnum,
  PermissionActionEnum,
} from '@scrambl/shared'

// Module hrefs that have a dedicated settings page (routes verified in
// organization-routes.tsx). Value is the org-relative settings path.
// Note: /notes/settings is a dead redirect and /tasks/settings just
// redirects to /tasks/mine/settings, so neither is listed.
export const MODULE_SETTINGS_PATHS: Record<string, string> = {
  '/tasks/mine': '/tasks/mine/settings',
  '/calendar': '/calendar/settings',
  '/projects': '/projects/settings',
  '/tickets': '/tickets/settings',
  '/events': '/events/settings',
  '/contacts': '/contacts/settings',
  '/deals': '/deals/settings',
  '/entities': '/entities/settings',
  '/suppliers': '/suppliers/settings',
  '/team': '/team/settings',
  '/timesheets': '/timesheets/settings',
  '/contracts': '/contracts/settings',
  '/invoices': '/invoices/settings',
  '/expenses': '/expenses/settings',
  '/quotes': '/quotes/settings',
  '/products': '/products/settings',
  '/knowledge-base': '/knowledge-base/settings',
}

/**
 * What a user needs to EDIT the settings behind an entry. Absent = personal
 * settings, always editable by their owner. The gate mirrors the target
 * page's own checkPermission call — keep them in sync when a page changes
 * its gate.
 */
export type SettingsEditGate =
  | { kind: 'module'; module: ModuleTypeEnum; action: PermissionActionEnum }
  | { kind: 'orgAdmin' }

export interface SettingsDirectoryEntry {
  /** Stable id: 'personal:profile', 'org:/tickets', 'admin:/admin/settings' */
  id: string
  scope: 'personal' | 'org' | 'admin'
  /**
   * For org entries: key into MODULE_SETTINGS_PATHS + MODULE_SETTINGS_SECTIONS
   * and the href of the matching NavItem in useNavigationItems (label, icon,
   * and module visibility are resolved from it). For admin entries: the href
   * of the matching adminNavItems item.
   */
  navHref?: string
  /** Display label for entries with no NavItem (personal entries). */
  label?: string
  description: string
  /**
   * Org-relative deep-link path for entries not derivable from
   * MODULE_SETTINGS_PATHS (personal + admin entries).
   */
  settingsPath?: string
  editGate?: SettingsEditGate
  /**
   * Section ids inside an org module settings page that are personal
   * preferences (never marked read-only in the hub, and still navigable
   * when the rest of the group is read-only).
   */
  personalSectionIds?: string[]
  /**
   * Section ids the target page gates on org admin + business account
   * rather than the module MANAGE permission (e.g. entities' client portal
   * sections). Hidden in the hub unless both hold.
   */
  orgAdminSectionIds?: string[]
  /** Mirrors NavItem.accountTypes for entries with no NavItem. */
  accountTypes?: AccountType[]
  keywords?: string[]
}

export const SETTINGS_DIRECTORY: SettingsDirectoryEntry[] = [
  // ---- Personal ----------------------------------------------------------
  {
    id: 'personal:profile',
    scope: 'personal',
    label: 'My Profile',
    description: 'Your profile, skills, notification preferences, and connected apps',
    settingsPath: '/user-settings',
    keywords: ['profile', 'notifications', 'integrations', 'skills', 'preferences'],
  },
  {
    id: 'personal:my-task-statuses',
    scope: 'personal',
    navHref: '/tasks/mine',
    label: 'My Work',
    description: 'Personal task statuses and your recycle bin',
    keywords: ['task statuses', 'my work', 'recycle bin'],
  },
  {
    id: 'personal:calendar',
    scope: 'personal',
    navHref: '/calendar',
    label: 'Calendar',
    description: 'Connect Google or Outlook calendars and choose which to show',
    keywords: ['calendar', 'google', 'outlook', 'sync'],
  },

  // ---- Org modules -------------------------------------------------------
  // editGate mirrors each settings page's own checkPermission call (verified
  // 2026-08: products gates on INVENTORY, timesheets on TIME_ENTRY).
  {
    id: 'org:/projects',
    scope: 'org',
    navHref: '/projects',
    description: 'Statuses, task types, cycle templates, custom fields, and defaults',
    editGate: { kind: 'module', module: ModuleTypeEnum.PROJECT, action: PermissionActionEnum.MANAGE },
    personalSectionIds: ['personal', 'notifications'],
  },
  {
    id: 'org:/tickets',
    scope: 'org',
    navHref: '/tickets',
    description: 'Ticket types, inboxes, domains, macros, alerts, and the customer widget',
    editGate: { kind: 'module', module: ModuleTypeEnum.TICKET, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/events',
    scope: 'org',
    navHref: '/events',
    description: 'Event, session, and agenda types, roles, reminders, and defaults',
    editGate: { kind: 'module', module: ModuleTypeEnum.EVENT, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/contacts',
    scope: 'org',
    navHref: '/contacts',
    description: 'Lifecycle, scoring, categories, custom fields, and defaults',
    editGate: { kind: 'module', module: ModuleTypeEnum.CONTACT, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/deals',
    scope: 'org',
    navHref: '/deals',
    description: 'Pipeline stages, scoring, custom fields, and defaults',
    editGate: { kind: 'module', module: ModuleTypeEnum.DEAL, action: PermissionActionEnum.MANAGE },
    personalSectionIds: ['personal'],
  },
  {
    id: 'org:/entities',
    scope: 'org',
    navHref: '/entities',
    description: 'Lifecycle, scoring, custom fields, and client portal configuration',
    editGate: { kind: 'module', module: ModuleTypeEnum.ENTITY, action: PermissionActionEnum.MANAGE },
    personalSectionIds: ['personal'],
    orgAdminSectionIds: ['portal-branding', 'portal-permission-templates'],
  },
  {
    id: 'org:/contracts',
    scope: 'org',
    navHref: '/contracts',
    description: 'Contract types, legal statuses, reminders, and retainer plans',
    editGate: { kind: 'module', module: ModuleTypeEnum.CONTRACT, action: PermissionActionEnum.MANAGE },
    personalSectionIds: ['personal'],
  },
  {
    id: 'org:/invoices',
    scope: 'org',
    navHref: '/invoices',
    description: 'Numbering, branding, sending, reminders, and payment methods',
    editGate: { kind: 'module', module: ModuleTypeEnum.INVOICE, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/team',
    scope: 'org',
    navHref: '/team',
    description: 'Rates, billing, allocation, holidays, compensation, and skills',
    editGate: { kind: 'module', module: ModuleTypeEnum.TEAM, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/timesheets',
    scope: 'org',
    navHref: '/timesheets',
    description: 'Time format, pay periods, entry rules, and approval workflow',
    editGate: { kind: 'module', module: ModuleTypeEnum.TIME_ENTRY, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/products',
    scope: 'org',
    navHref: '/products',
    description: 'Category types, movement reasons, and custom fields',
    editGate: { kind: 'module', module: ModuleTypeEnum.INVENTORY, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/suppliers',
    scope: 'org',
    navHref: '/suppliers',
    description: 'Lifecycle stages, payment terms, and custom fields',
    editGate: { kind: 'module', module: ModuleTypeEnum.SUPPLIER, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/expenses',
    scope: 'org',
    navHref: '/expenses',
    description: 'Categories, statuses, payment methods, and defaults',
    editGate: { kind: 'module', module: ModuleTypeEnum.EXPENSE, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/quotes',
    scope: 'org',
    navHref: '/quotes',
    description: 'Quote acceptance and invoicing behavior',
    editGate: { kind: 'module', module: ModuleTypeEnum.QUOTE, action: PermissionActionEnum.MANAGE },
  },
  {
    id: 'org:/knowledge-base',
    scope: 'org',
    navHref: '/knowledge-base',
    description: 'Delivery channels and the public help center',
    editGate: { kind: 'module', module: ModuleTypeEnum.KNOWLEDGE_BASE, action: PermissionActionEnum.MANAGE },
  },

  // ---- Administration ----------------------------------------------------
  // Admin entries carry explicit labels (the adminNavItems label for
  // /admin/settings is just "Settings", which is ambiguous inside the hub).
  {
    id: 'admin:/admin/members',
    scope: 'admin',
    navHref: '/admin/members',
    label: 'Members',
    description: 'Invite members, manage seats, and set access levels',
    settingsPath: '/admin/members',
    editGate: { kind: 'orgAdmin' },
    keywords: ['members', 'users', 'invite', 'roles', 'permissions', 'seats'],
  },
  {
    id: 'admin:/admin/integrations',
    scope: 'admin',
    navHref: '/admin/integrations',
    label: 'Integrations',
    description: 'Organization-wide connections like Slack and QuickBooks',
    settingsPath: '/admin/integrations',
    editGate: { kind: 'orgAdmin' },
    keywords: ['integrations', 'slack', 'quickbooks', 'connect'],
  },
  {
    id: 'admin:/admin/settings',
    scope: 'admin',
    navHref: '/admin/settings',
    label: 'Organization Settings',
    description: 'Organization identity, workspace defaults, and finance',
    settingsPath: '/admin/settings',
    editGate: { kind: 'orgAdmin' },
    keywords: ['organization', 'general', 'workspace', 'finance', 'timezone', 'terminology'],
  },
  {
    id: 'admin:/admin/subscription',
    scope: 'admin',
    navHref: '/admin/subscription',
    label: 'Subscription & Billing',
    description: 'Plan, seats, usage, and payment details',
    settingsPath: '/admin/subscription',
    editGate: { kind: 'orgAdmin' },
    accountTypes: [AccountType.ORGANIZATION],
    keywords: ['billing', 'plan', 'payment', 'upgrade', 'seats', 'subscription'],
  },
]
